import { Check, Loader2, RotateCcw, Save } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDateTime } from "@/lib/format";

/** Whether this card is still the shipped one, or how far it has moved. */
const StateBadge = ({ isDefault, changeCount }) =>
  isDefault ? (
    <Badge
      variant="outline"
      data-testid="rate-card-default-badge"
      className="border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] font-normal text-[color:var(--tt-teal-deep)]"
    >
      <Check className="mr-1 h-3 w-3" aria-hidden="true" />
      Shipped defaults
    </Badge>
  ) : (
    <Badge
      variant="outline"
      data-testid="rate-card-edited-badge"
      className="border-[color:var(--tt-warn-border)] bg-[color:var(--tt-warn-bg)] font-normal text-[color:var(--tt-warn-text)]"
    >
      {changeCount} change{changeCount === 1 ? "" : "s"} from default
    </Badge>
  );

/** Resetting throws away every edit, so it asks first. */
const ResetButton = ({ resetting, onReset }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button
        type="button"
        variant="outline"
        disabled={resetting}
        data-testid="rate-card-reset-button"
        className="h-10 bg-white"
      >
        <RotateCcw
          className={`mr-2 h-4 w-4 ${resetting ? "animate-spin" : ""}`}
          aria-hidden="true"
        />
        Reset to defaults
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent className="bg-white">
      <AlertDialogHeader>
        <AlertDialogTitle>Restore the shipped rate card?</AlertDialogTitle>
        <AlertDialogDescription>
          Every fee, band and ceiling goes back to the figures the calculator was built with.
          Quotes already issued keep the figures they were priced at — only new quotes are
          affected.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="rate-card-reset-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onReset} data-testid="rate-card-reset-confirm">
          Yes, reset it
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/** What this card is, when it last moved, and the save / reset controls. */
export const RateCardToolbar = ({ payload, changeCount, dirty, saving, resetting, onSave, onReset }) => (
  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
    <div>
      <h3 className="text-base font-bold text-foreground">Rate card</h3>
      <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Every fee, band and ceiling the calculator uses. Save a change and the very next quote is
        priced with it — no code, no deploy. Question wording stays in the app, so an edit here can
        never break a question.
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        {payload.updated_at ? (
          <>
            Last changed {formatDateTime(payload.updated_at)}
            {payload.updated_by ? ` by ${payload.updated_by}` : ""}
          </>
        ) : (
          "Never changed."
        )}
      </p>
    </div>

    <div className="flex flex-wrap items-center gap-2">
      <StateBadge isDefault={payload.is_default} changeCount={changeCount} />
      <ResetButton resetting={resetting} onReset={onReset} />
      <Button
        type="button"
        onClick={onSave}
        disabled={!dirty || saving}
        data-testid="rate-card-save-button"
        className="h-10 min-w-[150px]"
      >
        {saving ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Save className="mr-2 h-4 w-4" aria-hidden="true" />
        )}
        {dirty ? "Save changes" : "Saved"}
      </Button>
    </div>
  </div>
);
